import React, { useState, useEffect, useRef } from "react";
import {
  Eye,
  MoreVertical,
  Printer,
  PackageCheck,
  Send,
  CheckCircle2,
  Trash2,
  Plus,
  Loader,
  ArrowRight,
} from "lucide-react";
import SearchableSelect from "../SearchableSelect";

export default function PurchaseOrderTable({
  data = [],
  loading = false,
  error = null,
  currentPage = 1,
  itemsPerPage = 10,
  totalEntries = 0,
  startEntry = 0,
  endEntry = 0,
  maxPages = 1,
  selectedStatus = "ALL",
  onStatusFilterChange,
  onCreatePO,
  onDetail,
  onPrint,
  onQuickReceive,
  onUpdateStatus,
  onPageChange,
  onItemsPerPageChange,
  onSort,
  sortBy,
  sortOrder,
}) {
  const [activeMenuId, setActiveMenuId] = useState(null);
  const menuRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setActiveMenuId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const statusPills = [
    { key: "ALL", label: "Semua Status" },
    { key: "DRAFT", label: "Draft" },
    { key: "SUBMITTED", label: "Menunggu Persetujuan" },
    { key: "APPROVED", label: "Disetujui" },
    { key: "ORDERED", label: "Sedang Dipesan" },
    { key: "PARTIAL_RECEIVED", label: "Diterima Sebagian" },
    { key: "COMPLETED", label: "Selesai Diterima" },
    { key: "CANCELLED", label: "Dibatalkan" },
  ];

  const getStatusBadge = (status) => {
    switch (status?.toUpperCase()) {
      case "DRAFT":
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 ring-1 ring-slate-200">
            Draft
          </span>
        );
      case "SUBMITTED":
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 ring-1 ring-amber-200">
            Menunggu Persetujuan
          </span>
        );
      case "APPROVED":
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-sky-50 text-sky-700 ring-1 ring-sky-200">
            Disetujui
          </span>
        );
      case "ORDERED":
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200">
            Sedang Dipesan
          </span>
        );
      case "PARTIAL_RECEIVED":
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 ring-1 ring-amber-200">
            Diterima Sebagian
          </span>
        );
      case "COMPLETED":
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200">
            Selesai Diterima
          </span>
        );
      case "CANCELLED":
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 ring-1 ring-rose-300">
            Dibatalkan
          </span>
        );
      case "REJECTED":
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 ring-1 ring-rose-300">
            Ditolak
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 ring-1 ring-slate-200">
            {status || "-"}
          </span>
        );
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat("id-ID", {
        day: "numeric",
        month: "short",
        year: "numeric",
      }).format(date);
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null || isNaN(amount)) return "Rp 0";
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const totalBelanjaKeseluruhan = data.reduce((sum, item) => {
    const val = item.total_estimated_amount ?? item.total_amount ?? 0;
    return sum + (typeof val === "number" ? val : Number(val) || 0);
  }, 0);

  const SortIcon = ({ column }) => {
    if (sortBy !== column) return <span className="text-gray-300 ml-1">↕</span>;
    return sortOrder === "asc" ? (
      <span className="text-teal-600 ml-1">↑</span>
    ) : (
      <span className="text-teal-600 ml-1">↓</span>
    );
  };

  const toggleMenu = (id, e) => {
    e.stopPropagation();
    setActiveMenuId((prev) => (prev === id ? null : id));
  };

  return (
    <div className="bg-white rounded-2xl ring-1 ring-slate-200/70 shadow-sm overflow-hidden mt-6">
      {/* Loading State */}
        {loading && (
          <div className="px-6 py-16 flex items-center justify-center text-gray-500">
            <Loader className="w-6 h-6 animate-spin text-teal-600 mr-2" />
            <span>Memuat data purchase order...</span>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="px-6 py-4 bg-rose-50 border-b border-rose-200 text-rose-700 text-sm">
            Error: {error}
          </div>
        )}

        {/* Table Content */}
        {!loading && !error && (
          <>
            <div className="overflow-x-auto overflow-y-visible">
              <table className="w-full text-left">
                <thead className="bg-gray-50/80 border-b border-gray-200">
                  <tr>
                    <th
                      onClick={() => onSort && onSort("po_number")}
                      className="px-4 py-3 text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100">
                      NO. PO <SortIcon column="po_number" />
                    </th>
                    <th
                      onClick={() => onSort && onSort("supplier")}
                      className="px-4 py-3 text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100">
                      SUPPLIER <SortIcon column="supplier" />
                    </th>
                    <th
                      onClick={() => onSort && onSort("request_date")}
                      className="px-4 py-3 text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100">
                      TGL ORDER <SortIcon column="request_date" />
                    </th>
                    <th
                      onClick={() => onSort && onSort("due_date")}
                      className="px-4 py-3 text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100">
                      TGL ESTIMASI <SortIcon column="due_date" />
                    </th>
                    <th className="px-4 py-3 text-xs font-bold text-gray-700 uppercase tracking-wider text-right">
                      TOTAL BELANJA
                    </th>
                    <th
                      onClick={() => onSort && onSort("status")}
                      className="px-4 py-3 text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 text-center">
                      STATUS <SortIcon column="status" />
                    </th>
                    <th className="px-4 py-3 text-xs font-bold text-gray-700 uppercase tracking-wider text-center w-28">
                      AKSI
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {data.length === 0 ? (
                    <tr>
                      <td
                        colSpan="7"
                        className="px-6 py-16 text-center text-gray-400 text-sm">
                        Tidak ada data purchase order yang ditemukan
                      </td>
                    </tr>
                  ) : (
                    data.map((item) => {
                      const totalAmount =
                        item.total_estimated_amount ?? item.total_amount ?? 0;
                      const isMenuOpen = activeMenuId === item.id;
                      const statusUpper = item.status?.toUpperCase();
                      const isDraft =
                        statusUpper === "DRAFT" || statusUpper === "SUBMITTED";
                      const isApproved = statusUpper === "APPROVED";
                      const isShippedOrPartial =
                        statusUpper === "ORDERED" ||
                        statusUpper === "PARTIAL_RECEIVED";
                      const isCompletedOrCancelled =
                        statusUpper === "COMPLETED" ||
                        statusUpper === "CANCELLED" ||
                        statusUpper === "REJECTED";

                      return (
                        <tr
                          key={item.id}
                          className="hover:bg-slate-50 transition-colors">
                          {/* No. PO */}
                          <td className="px-4 py-3 text-sm font-bold text-slate-900">
                            {item.po_number || "-"}
                          </td>

                          {/* Supplier */}
                          <td className="px-4 py-3 text-sm text-slate-800 font-medium">
                            {item.supplier?.name || item.supplier_name || "-"}
                          </td>

                          {/* Tgl Order */}
                          <td className="px-4 py-3 text-sm text-slate-600">
                            {formatDate(item.request_date || item.created_at)}
                          </td>

                          {/* Tgl Estimasi */}
                          <td className="px-4 py-3 text-sm text-slate-600">
                            {formatDate(item.due_date)}
                          </td>

                          {/* Total Belanja */}
                          <td className="px-4 py-3 text-sm font-bold text-slate-900 text-right">
                            {formatCurrency(totalAmount)}
                          </td>

                          {/* Status */}
                          <td className="px-4 py-3 text-center">
                            {getStatusBadge(item.status)}
                          </td>

                          {/* Aksi */}
                          <td className="px-4 py-3 text-center relative">
                            <div className="flex items-center justify-center gap-1.5">
                              {/* Eye Button (Detail) */}
                              <button
                                type="button"
                                onClick={() => onDetail && onDetail(item.id)}
                                className="w-8 h-8 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 flex items-center justify-center transition-colors shadow-sm"
                                title="Lihat Detail PO">
                                <Eye className="w-4 h-4" />
                              </button>

                              {/* Three-Dots Menu Button */}
                              <button
                                type="button"
                                onClick={(e) => toggleMenu(item.id, e)}
                                className={`w-8 h-8 rounded-lg border flex items-center justify-center transition-colors shadow-sm ${
                                  isMenuOpen
                                    ? "bg-slate-900 text-white border-slate-900"
                                    : "border-slate-300 bg-white hover:bg-slate-50 text-slate-700"
                                }`}
                                title="Aksi Lainnya">
                                <MoreVertical className="w-4 h-4" />
                              </button>
                            </div>

                            {/* Floating Action Menu Popover */}
                            {isMenuOpen && (
                              <div
                                ref={menuRef}
                                className="absolute right-4 top-12 z-50 w-48 bg-white rounded-2xl shadow-2xl ring-1 ring-slate-200 p-2 space-y-1 text-left animate-in fade-in zoom-in-95 duration-100">
                                {/* Cetak PO */}
                                <button
                                  type="button"
                                  onClick={() => {
                                    setActiveMenuId(null);
                                    onPrint && onPrint(item.id);
                                  }}
                                  className="w-full px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 rounded-lg flex items-center gap-2 transition">
                                  <Printer className="w-4 h-4 text-slate-500" />
                                  <span>Cetak PO</span>
                                </button>

                                {/* Action: Draft -> Setujui PO */}
                                {isDraft && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setActiveMenuId(null);
                                      onUpdateStatus &&
                                        onUpdateStatus(item.id, "APPROVED");
                                    }}
                                    className="w-full px-3 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 rounded-lg flex items-center gap-2 transition">
                                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                                    <span>Setujui PO</span>
                                  </button>
                                )}

                                {/* Action: Approved -> Kirim PO */}
                                {isApproved && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setActiveMenuId(null);
                                      onUpdateStatus &&
                                        onUpdateStatus(item.id, "ORDERED");
                                    }}
                                    className="w-full px-3 py-2 text-xs font-semibold text-orange-600 hover:bg-orange-50 rounded-lg flex items-center gap-2 transition">
                                    <ArrowRight className="w-4 h-4 text-orange-600" />
                                    <span>Kirim PO</span>
                                  </button>
                                )}

                                {/* Action: Shipped -> Terima Barang */}
                                {isShippedOrPartial && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setActiveMenuId(null);
                                      onQuickReceive &&
                                        onQuickReceive(item.id);
                                    }}
                                    className="w-full px-3 py-2 text-xs font-semibold text-teal-700 hover:bg-teal-50 rounded-lg flex items-center gap-2 transition">
                                    <PackageCheck className="w-4 h-4 text-teal-600" />
                                    <span>Terima Barang</span>
                                  </button>
                                )}

                                {/* Action: Batalkan PO (Red Solid Button) */}
                                {!isCompletedOrCancelled && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setActiveMenuId(null);
                                      if (
                                        window.confirm(
                                          "Apakah Anda yakin ingin membatalkan Purchase Order ini?",
                                        )
                                      ) {
                                        onUpdateStatus &&
                                          onUpdateStatus(item.id, "CANCELLED");
                                      }
                                    }}
                                    className="w-full px-3 py-2.5 text-xs font-bold text-white bg-rose-600 hover:bg-rose-700 rounded-xl flex items-center gap-2 transition shadow-sm shadow-rose-600/30 mt-1">
                                    <Trash2 className="w-4 h-4 text-white" />
                                    <span>Batalkan PO</span>
                                  </button>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Bottom Summary & Pagination Controls */}
            <div className="px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs font-semibold text-slate-600">
              <div className="flex items-center gap-4">
                <span>Total: {totalEntries} Purchase Order</span>
                <span>Total Belanja: {formatCurrency(totalBelanjaKeseluruhan)}</span>
              </div>

              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 mr-2">
                  <SearchableSelect
                    options={[
                      { value: 10, label: "10" },
                      { value: 25, label: "25" },
                      { value: 50, label: "50" },
                      { value: 100, label: "100" },
                    ]}
                    value={itemsPerPage}
                    onChange={(value) =>
                      onItemsPerPageChange &&
                      onItemsPerPageChange(Number(value))
                    }
                    placeholder="Per page"
                    displayKey="label"
                    valueKey="value"
                    className="w-20"
                    searchable={false}
                    size="sm"
                  />
                </div>
                <button
                  type="button"
                  disabled={currentPage <= 1}
                  onClick={() => onPageChange && onPageChange(currentPage - 1)}
                  className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
                  ‹ Prev
                </button>
                <span className="px-3 py-1.5 text-xs font-bold text-teal-700 bg-teal-50 rounded-lg">
                  {currentPage} / {maxPages || 1}
                </span>
                <button
                  type="button"
                  disabled={currentPage >= maxPages}
                  onClick={() => onPageChange && onPageChange(currentPage + 1)}
                  className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
                  Next ›
                </button>
              </div>
            </div>
          </>
        )}
      </div>
  );
}
